import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICompany } from 'src/app/core/models/common/company.model';
import { IUser, UserRole } from 'src/app/core/models/user.model';
import { environment } from '../../../environments/environment';
import { IGroup } from '../../core/models/common/group.model';
import { GroupsUtils } from '../../utils/groups/groups.utils';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  readonly USER_URI = 'users';
  readonly COMPANIES_URI = 'companies';
  public self: IUser;
  public ownedGroups: IGroup[] = [];
  public selectedAccount = new Subject<void>();
  public selectedAccountForParent = new Subject<void>();

  constructor(private readonly http: HttpClient,
              private readonly router: Router
  ) {
  }

  /**
   * Get signed in user
   * @returns Observable<IUser>
   */
  public getSignedInUser(): Observable<IUser> {
    return this.http.get(`${environment.api_base_urls.ms_users}v2/user-details/me`).pipe(
      tap((self: IUser) => {
        this.self = self;
      })
    );
  }

  public isGroupView(): boolean {
    return !this.self || !this.self.company;
  }

  public isOrphanCompany(): boolean {
    return !this.self.group && !this.self.company?.groups?.length;
  }

  public isGroupCompaniesEmpty(): boolean {
    return this.isGroupView() && !this.self.group?.companies?.length;
  }

  public getCurrentUserId(): number {
    return this.self?.id;
  }

  public getCurrentGroupId(): number {
    return this.self?.group?.id;
  }

  public getCurrentCompanyId(): number {
    return this.self?.company?.id;
  }

  public isAdmin(): boolean {
    return this.self && this.self.userRole === UserRole.ADMINISTRATOR;
  }

  public getGroupCompanies(): Observable<ICompany[]> {
    return this.http.get<ICompany[]>(`${environment.api_base_urls.ms_users}${this.COMPANIES_URI}/currents`);
  }

  /**
   * switch current company or group
   * @param entityId identifier of the company or group to switch on
   * @param entityType entity type
   */
  public switchEntity(entityId: number, entityType: 'group' | 'company'): void {
    this.http.patch(`${environment.api_base_urls.ms_users}v2/${this.USER_URI}/${this.self?.id}/${entityType}`, entityId).subscribe(
      () => {
        this.getSignedInUser().subscribe(() => {
          const urlFound: { found: boolean, contains: string } = GroupsUtils.isInGroupWhitelist(this.router.url);
          if (this.isGroupView() && !urlFound.found) {
            // redirect to the nearest authorized path
            this.router.navigate([`/private/${urlFound.contains}`]);
            // Used to notify parents components to check the user rights so that they can reload their views
            this.selectedAccountForParent.next();
          } else {
            // Vue company : si on arrive d'une route groupe-exclusive, rediriger vers le dashboard
            if (!this.isGroupView() && urlFound.found && GroupsUtils.isGroupOnlyRoute(urlFound.contains)) {
              this.router.navigate(['/private/dashboard']);
            }
            // Used to notify currents components and parents that the user changed so that
            // they can reload their views/data
            this.selectedAccount.next();
          }
        });
      });
  }

  public getUserGroups(): Observable<IGroup[]> {
    return this.http.get<IGroup[]>(`${environment.api_base_urls.ms_users}groups/own`).pipe(
      tap((groups: IGroup[]) => {
        this.ownedGroups = groups;
      })
    );
  }

  public currentUserCompany(): Observable<ICompany> {
    return this.getSignedInUser().pipe(
      map((user) => (user ? user.company : undefined))
    );
  }

  public buildEntityUrlSegment(current: boolean = false) {
    if (this.isGroupView()) {
      return `groups/${this.self?.group?.id}`;
    } else if (current) {
      return `companies/current`;
    } else {
      return `companies/${this.self?.company?.id}`;
    }
  }

  public buildEntityMembersUrlSegment(): string {
    return this.buildEntityMembersUrlSegmentForSpecificMember(this.self?.memberId);
  }

  public buildEntityMembersUrlSegmentForSpecificMember(specificMemberId: number): string {
    return `${this.buildRawEntityMembersUrlSegment()}/${specificMemberId}`;
  }

  public buildRawEntityMembersUrlSegment(): string {
    if (this.isGroupView()) {
      return `group-members`;
    } else {
      return `company-members`;
    }
  }
}
